import { useEffect, useState } from 'react';
import { FAQPageJsonLd } from 'next-seo';
import RemoveMd from 'remove-markdown';
import clsx from 'clsx';

import Link from 'components/common/Link';
import ParagraphHeader from 'components/ParagraphHeader';
import Button from 'components/common/Button';

import { FaqAndSeoTabProps, FAQProps, FAQTab } from 'interfaces/cms/content';
import Tab from 'components/common/Tab';
import Panel from 'components/common/Tab/Panel';
import useMediaQuery from 'hooks/useMediaQuery';
import Image from 'components/common/Image';

import FaqItem from '../FaqAndSeo/FaqItem';

function FaqAndSeoTab({
  bg,
  header,
  faqTabs,
  button,
  banner,
}: FaqAndSeoTabProps) {
  const [isMobile] = useMediaQuery(639);
  const getFaqStructureData = () => {
    const faqData: any[] = [];
    faqTabs?.forEach((faqTab) => {
      faqTab?.faqs?.forEach((faq) => {
        faqData.push({
          questionName: RemoveMd(faq.title).replace(/['"]+/g, ''),
          acceptedAnswerText: faq.customEditor,
        });
      });
    });
    return faqData;
  };

  const [openFaqIds, setOpenFaqIds] = useState<any[]>([]);
  const [tabParam, setTabParam] = useState(0);

  const onOpenFaq = (id: any) => {
    if (openFaqIds.some((openId) => openId === id)) {
      setOpenFaqIds((prev) => prev.filter((openId) => openId !== id));
    } else {
      setOpenFaqIds([...openFaqIds, id]);
    }
  };

  useEffect(() => {
    const autoExpandFaqs: any[] = [];
    const query = new URLSearchParams(document.location.search);
    const faqIdParam = Number(query.get('faq_id'));
    setTabParam(Number(query.get('tab_num')));

    faqTabs?.forEach((faqTab) =>
      faqTab?.faqs?.map((faq) => {
        if (faq.autoExpand) {
          autoExpandFaqs.push(faq.id);
        }
      })
    );
    setOpenFaqIds(autoExpandFaqs);

    if (faqIdParam) {
      autoExpandFaqs.push(faqIdParam);
    }
  }, [faqTabs]);

  function getFaqTabContent(faqTab: FAQTab, tabNum: number) {
    return (
      <div className="w-full md:w-[45.625rem] mx-auto">
        {faqTab?.faqs?.map((faq: FAQProps) => (
          <FaqItem
            key={faq.id}
            {...{ ...faq, openFaqIds, onOpenFaq, tabNum }}
          />
        ))}
      </div>
    );
  }

  function getFaqTabArea() {
    return (
      <>
        <Tab default={tabParam || 0} banner={banner}>
          {Number(faqTabs?.length) > 1
            ? faqTabs?.map((faqTab, tabNum) => (
                <Panel key={`Tab-${faqTab.id}`} title={faqTab.title}>
                  {getFaqTabContent(faqTab, tabNum)}
                </Panel>
              ))
            : faqTabs?.map((faqTab, tabNum) =>
                getFaqTabContent(faqTab, tabNum)
              )}
        </Tab>

        {(button?.buttonUrl || button?.buttonPageSlug?.slug) && (
          <div className="text-center mt-[1.875rem]">
            <Link
              href={button.buttonPageSlug?.slug || button.buttonUrl}
              rel={button.buttonLinkRel}
              isExternalLink={!!button.buttonUrl}
            >
              <Button
                {...button}
                buttonType="outline"
                btnStyle="min-w-[70%] md:min-w-[21.875rem]"
              />
            </Link>
          </div>
        )}
      </>
    );
  }

  function getFaqBanner() {
    return (
      <div
        className={clsx(
          'text-center flex-1 inline-grid',
          isMobile
            ? 'px-4 justify-center'
            : banner?.image.picturePosition === 'left'
              ? 'justify-end mr-8'
              : 'justify-start ml-8',
          Number(faqTabs?.length) > 1 && !isMobile && 'mt-[72px]',
          isMobile && banner?.image.picturePosition === 'right' && 'pt-4',
          isMobile && banner?.image.picturePosition === 'left' && 'pb-4'
        )}
      >
        {!isMobile && banner?.image.desktopImage && (
          <Image
            src={`${banner?.image.desktopImage.url}`}
            alt={banner?.image.desktopImage.alt}
            width={banner?.image.desktopImage.width}
            height={banner?.image.desktopImage.height}
          />
        )}
        {isMobile && banner?.image.mobileImage && (
          <Image
            src={`${banner?.image.mobileImage.url}`}
            alt={banner?.image.mobileImage.alt}
            width={banner?.image.desktopImage.width}
            height={banner?.image.desktopImage.height}
          />
        )}
      </div>
    );
  }

  return (
    <>
      <FAQPageJsonLd mainEntity={getFaqStructureData()} />

      <div
        className="px-[0.9375rem] py-[1.875rem]"
        style={{ backgroundColor: bg }}
      >
        {header && (
          <>
            <ParagraphHeader {...header} />
            <div className="mb-[1.875rem]" />
          </>
        )}

        {banner?.image ? (
          <div
            className={clsx(
              'max-w-[69.375rem] mx-auto flex',
              isMobile && 'flex-col'
            )}
          >
            {banner?.image.picturePosition === 'left' && getFaqBanner()}
            <div className="flex-1">{getFaqTabArea()}</div>
            {banner?.image.picturePosition === 'right' && getFaqBanner()}
          </div>
        ) : (
          getFaqTabArea()
        )}
      </div>
    </>
  );
}

export default FaqAndSeoTab;
